import { Suspense, useEffect, type ReactNode } from "react";
import { ErrorBoundary } from "react-error-boundary";
import { Menu } from "lucide-react";
import { Link, useLocation } from "react-router";
import { useGetIdentity } from "ra-core";
import { Avatar, AvatarFallback, AvatarImage } from "@/components/ui/avatar";
import { Notification } from "@/components/admin/notification";
import { Error } from "@/components/admin/error";
import { Skeleton } from "@/components/ui/skeleton";
import { Button } from "@/components/ui/button";
import { useConfigurationContext } from "../root/ConfigurationContext";
import { cn } from "@/lib/utils";
import { rotaPermitidaLeitura, useSomenteLeitura } from "../commons/somenteLeitura";
import { AcessoNaoPermitido } from "../misc/AcessoNaoPermitido";

import { ContadorNovos, Sidebar, TrocaProduto } from "./Sidebar";
import { SinoNotificacoes } from "../feed/SinoNotificacoes";
import { useFeedNaoLidos } from "../feed/useFeedNaoLidos";
import { useConversas } from "../mensagens/useConversas";
import { mensagensVisiveis } from "../mensagens/liberacao";
import { useSidebar } from "./SidebarContext";
import { useSessaoUnica } from "../commons/useSessaoUnica";
import { FaixaVerComo, VerComo } from "./VerComo";
import { SeletorUnidade } from "../unidades/SeletorUnidade";
import { AplicarMarca, ehCliente, useOrganizacaoAtiva } from "../organizacoes/useOrganizacaoAtiva";

/**
 * Botão do menu no celular: o menu fica fechado, então o número de não lidos
 * do Feed e das Mensagens sobe para o botão. Componente à parte para só
 * consultar quando o cabeçalho do celular está na tela.
 */
const BotaoMenuCelular = ({ abrir }: { abrir: () => void }) => {
  const { total: feed } = useFeedNaoLidos();
  const { total: mensagens } = useConversas({ ativo: mensagensVisiveis() });
  const total = feed + mensagens;
  return (
    <Button
      variant="ghost"
      size="sm"
      className="relative h-9 w-9 p-0 text-white hover:bg-white/10 hover:text-white dark:text-foreground dark:hover:bg-muted"
      onClick={abrir}
      aria-label={total ? `Abrir menu: ${total} não ${total === 1 ? "lido" : "lidos"}` : "Abrir menu"}
    >
      <Menu className="w-5 h-5" />
      {total > 0 && <ContadorNovos total={total} />}
    </Button>
  );
};

/** A pessoa logada: foto e nome (antes no rodapé do menu); leva a Meu perfil. */
const AvatarDaBarra = () => {
  const { identity } = useGetIdentity();
  if (!identity) return null;
  const somenteLeitura = (identity.perfil_leitura ?? null) !== null;
  const conteudo = (
    <>
      <span className="hidden min-w-0 text-right leading-tight md:block">
        <span className="block truncate text-[13px] font-semibold text-white dark:text-foreground">{identity.fullName}</span>
        <span className="block truncate text-[11px] text-white/70 dark:text-muted-foreground">{identity.email}</span>
      </span>
      <Avatar className="h-8 w-8">
        <AvatarImage src={identity.avatar} alt={identity.fullName} />
        <AvatarFallback className="bg-[#F4ECDD] text-[12px] font-semibold text-[#7A6440]">
          {identity.fullName?.charAt(0) || "U"}
        </AvatarFallback>
      </Avatar>
    </>
  );
  // Perfil de leitura não tem Meu perfil (como no menu de antes): só mostra quem é.
  return somenteLeitura ? (
    <span className="flex items-center gap-2.5">{conteudo}</span>
  ) : (
    <Link to="/settings" className="flex cursor-pointer items-center gap-2.5 rounded-lg px-1.5 py-1 hover:bg-white/10 dark:hover:bg-muted" title="Meu perfil">
      {conteudo}
    </Link>
  );
};

export const Layout = ({ children }: { children: ReactNode }) => {
  const { collapsed, isMobile, mobileOpen, setMobileOpen } = useSidebar();
  // Uma sessão por pessoa no escritório cliente (migration 282).
  useSessaoUnica();
  const { darkModeLogo, title } = useConfigurationContext();
  const location = useLocation();
  // Escritório cliente (279): logo e cor dele, sem a marca d'água da North.
  const { data: organizacao } = useOrganizacaoAtiva();
  const cliente = ehCliente(organizacao);
  const logoBarra = organizacao === undefined ? null : cliente ? organizacao?.logo_url : darkModeLogo;

  useEffect(() => {
    if (mobileOpen) setMobileOpen(false);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [location.pathname]);

  // Perfil de leitura em tela fora do perfil: a mensagem no lugar da tela, com
  // o menu em volta. Enquanto a identidade não chega, nada de tela — é ela que
  // diz se a tela é permitida.
  const { perfil, isPending: identidadePendente } = useSomenteLeitura();
  const conteudo = identidadePendente
    ? null
    : perfil && !rotaPermitidaLeitura(perfil, location.pathname)
      ? <AcessoNaoPermitido />
      : children;

  return (
    <div className="flex h-screen w-screen overflow-hidden">
      <AplicarMarca />
      <Sidebar />
      <div
        className={cn(
          "relative flex-1 flex flex-col min-w-0 overflow-hidden transition-all duration-300 bg-pagina dark:bg-card",
          isMobile ? "ml-0" : collapsed ? "ml-20" : "ml-64"
        )}
      >
        {/* Marca d'água da North atrás do conteúdo de todas as páginas. Fica no
            container (que não rola), não no <main>, pra não deslizar junto com
            a rolagem — e o <main> é transparente de propósito: é o que deixa
            ela aparecer no cinza do bento (pedido do usuário, 2026-08-25).
            No claro a logo branca vira silhueta preta (brightness 0) a 7% —
            um cinza um tom abaixo do fundo; no escuro fica branca a 4%. */}
        {organizacao !== undefined && !cliente && <div
          aria-hidden
          className="pointer-events-none absolute inset-0 z-0 opacity-[0.07] [filter:brightness(0)] dark:opacity-[0.04] dark:[filter:none]"
          style={{
            backgroundImage: "url('/logos/logo-mark-white.png')",
            backgroundRepeat: "no-repeat",
            backgroundPosition: "center",
            backgroundSize: "min(60vh, 60vw) auto",
          }}
        />}
        {/* Barra superior (Premium North): troca de produto, sino e a pessoa.
            No celular ganha o botão do menu, com o total de não lidos. Fundo
            marinho desde 22/10/2026 (quebrar o branco), então logo e ícones
            vão na versão clara. */}
        <header className="relative z-30 flex h-14 shrink-0 items-center gap-2 border-b border-white/10 bg-marinho px-3 text-white sm:px-6 dark:border-border dark:bg-card dark:text-foreground">
          {isMobile && (
            <>
              <BotaoMenuCelular abrir={() => setMobileOpen(true)} />
              {logoBarra && <img className="h-7 max-w-[96px] object-contain" src={logoBarra} alt={cliente ? organizacao?.nome : title} />}
            </>
          )}
          <TrocaProduto />
          <div className="ml-auto flex items-center gap-1.5">
            <VerComo />
            <SeletorUnidade />
            <SinoNotificacoes tom="escuro" />
            <AvatarDaBarra />
          </div>
        </header>
        <FaixaVerComo />
        <main className="relative z-10 flex-1 overflow-y-auto bg-transparent" id="main-content">
          {/* Transparente: o cinza do bento mora no container acima, atrás da
              marca d'água — um <main> com fundo próprio taparia a logo. */}
          <div className="entrada-blocos mx-auto min-h-full w-full max-w-[1800px] p-3 sm:p-6 pb-seguro">
            <ErrorBoundary FallbackComponent={Error}>
              <Suspense fallback={<Skeleton className="h-12 w-12 rounded-full" />}>
                {conteudo}
              </Suspense>
            </ErrorBoundary>
          </div>
        </main>
      </div>
      <Notification />
    </div>
  );
};
